import type { CSSProperties } from 'react';
import SVG from 'react-inlinesvg';

const scribbleSources = {
    knot: '/auth/assets/svgs/knot.svg',
    rainbow: '/auth/assets/svgs/rainbow.svg',
    scribble: '/auth/assets/svgs/scribble.svg',
    squiggle: '/auth/assets/svgs/squiggle.svg',
    'squiggle-curl-2': '/auth/assets/svgs/squiggle-curl-2.svg',
} as const;

export type ScribbleType = keyof typeof scribbleSources;

type ScribbleCssProperties = CSSProperties & {
    '--scribble-duration'?: string;
    '--scribble-delay'?: string;
    '--scribble-stagger'?: string;
    '--scribble-fill-duration'?: string;
};

type AnimatedScribbleProps = {
    type?: ScribbleType;
    src?: string;
    className?: string;
    durationMs?: number;
    delayMs?: number;
    staggerMs?: number;
    fillRevealMs?: number;
    fillRevealOverlapMs?: number;
    revealFilledShapes?: boolean;
    loop?: boolean;
    ariaLabel?: string;
    preserveAspectRatio?: string;
};

export type AnimatedStrokeSvgProps = Omit<AnimatedScribbleProps, 'type' | 'src'> & {
    src: string;
};

const drawableSelector = 'path,line,polyline,polygon,circle,ellipse,rect';

function hasDrawableStroke(element: SVGGeometryElement) {
    const stroke = element.getAttribute('stroke');

    return Boolean(stroke && stroke.trim().toLowerCase() !== 'none');
}

function hasDrawableFill(element: SVGGeometryElement) {
    const fill = element.getAttribute('fill');

    return Boolean(fill && fill.trim().toLowerCase() !== 'none');
}

function prepareDrawableSvg(
    svgMarkup: string,
    staggerMs: number,
    durationMs: number,
    revealFilledShapes: boolean,
    fillRevealOverlapMs: number,
) {
    const parser = new DOMParser();
    const document = parser.parseFromString(svgMarkup, 'image/svg+xml');
    const svg = document.querySelector('svg');

    if (!svg || document.querySelector('parsererror')) {
        return svgMarkup;
    }

    let drawableIndex = 0;
    let fillIndex = 0;
    const fillRevealDelayMs = Math.max(0, durationMs - fillRevealOverlapMs);

    svg.querySelectorAll<SVGGeometryElement>(drawableSelector).forEach((element) => {
        if (!hasDrawableStroke(element)) {
            return;
        }

        element.classList.add('animated-scribble__path');
        element.setAttribute('pathLength', '1');
        element.style.setProperty(
            '--scribble-path-delay',
            `calc(var(--scribble-delay) + ${drawableIndex * staggerMs}ms)`,
        );
        drawableIndex += 1;
    });

    if (revealFilledShapes) {
        svg.querySelectorAll<SVGGeometryElement>(drawableSelector).forEach((element) => {
            if (hasDrawableStroke(element) || !hasDrawableFill(element)) {
                return;
            }

            element.classList.add('animated-scribble__fill');
            element.style.setProperty(
                '--scribble-fill-delay',
                `calc(var(--scribble-delay) + ${fillRevealDelayMs + fillIndex * staggerMs}ms)`,
            );
            fillIndex += 1;
        });
    }

    return new XMLSerializer().serializeToString(svg);
}

export function AnimatedStrokeSvg({
    src,
    className,
    durationMs = 1600,
    delayMs = 0,
    staggerMs = 90,
    fillRevealMs = 800,
    fillRevealOverlapMs = 250,
    revealFilledShapes = false,
    loop = false,
    ariaLabel,
    preserveAspectRatio = 'xMidYMid meet',
}: AnimatedStrokeSvgProps) {
    const style: ScribbleCssProperties = {
        '--scribble-duration': `${durationMs}ms`,
        '--scribble-delay': `${delayMs}ms`,
        '--scribble-stagger': `${staggerMs}ms`,
        '--scribble-fill-duration': `${fillRevealMs}ms`,
    };

    return (
        <SVG
            aria-hidden={ariaLabel ? undefined : true}
            aria-label={ariaLabel}
            className={['animated-scribble', loop && 'animated-scribble--loop', className]
                .filter(Boolean)
                .join(' ')}
            focusable="false"
            preserveAspectRatio={preserveAspectRatio}
            preProcessor={(code) =>
                prepareDrawableSvg(
                    code,
                    staggerMs,
                    durationMs,
                    revealFilledShapes,
                    fillRevealOverlapMs,
                )
            }
            role={ariaLabel ? 'img' : undefined}
            src={src}
            style={style}
        />
    );
}

export function AnimatedScribble({ type = 'scribble', src, ...props }: AnimatedScribbleProps) {
    return <AnimatedStrokeSvg src={src ?? scribbleSources[type]} {...props} />;
}

export const availableScribbles = Object.keys(scribbleSources) as ScribbleType[];
